import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import * as CustomerActions from '../_actions/customer';
import { Customer } from '../_models/customer';

export interface CustomerState extends EntityState<Customer> {
  loading: boolean;
  error: any;
}

/**
 * Entity adapter for customers.
 * No sortComparer is used - API returns data sorted by dateCreated DESC.
 * New items are manually prepended in addCustomerSuccess reducer.
 */
export const adapter: EntityAdapter<Customer> = createEntityAdapter<Customer>();

const initialState: CustomerState = adapter.getInitialState({
  loading: false,
  error: null,
});

const customerReducer = createReducer(
  initialState,

  on(CustomerActions.loadCustomers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(CustomerActions.loadCustomersSuccess, (state, { customers }) =>
    adapter.setAll(customers, { ...state, loading: false })
  ),

  on(CustomerActions.loadCustomersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  /**
   * Manually prepend new customer to the beginning of the list.
   * Direct state manipulation is used for performance (O(n) prepend vs O(n log n) sort).
   * Filter removes duplicate ID if present, then prepends new ID to maintain newest-first order.
   */
  on(CustomerActions.addCustomerSuccess, (state, { customer }) => ({
    ...state,
    entities: { ...state.entities, [customer.id]: customer },
    ids: [customer.id, ...(state.ids as number[]).filter((id) => id !== customer.id)],
  })),

  on(CustomerActions.updateCustomerSuccess, (state, { customer }) =>
    adapter.upsertOne(customer, state)
  ),

  on(CustomerActions.deleteCustomerSuccess, (state, { id }) => adapter.removeOne(id, state)),

  on(
    CustomerActions.addCustomerFailure,
    CustomerActions.updateCustomerFailure,
    CustomerActions.deleteCustomerFailure,
    (state, { error }) => ({
      ...state,
      error,
    })
  )
);

export function reducer(state: CustomerState | undefined, action: Action) {
  return customerReducer(state, action);
}

export const { selectAll, selectEntities, selectTotal } = adapter.getSelectors();
